/**
 * Product Upsell System
 * Intercepts add-to-cart and shows upsell modal
 */

(function() {
  'use strict';

  // ===== CONFIGURATION =====
  
  // GLOBAL CONFIG BY PRODUCT TYPE
  // Empty - use Theme Settings instead (Admin UI)
  // Example format (for reference only):
  // {
  //   'Seilarmband': {
  //     enabled: true,
  //     upsells: [
  //       { id: 'necklace', collection: 'circle-halsketten' }
  //     ],
  //     addons: ['geschenkverpackung-mit-schleife']
  //   }
  // }
  const GLOBAL_CONFIG_BY_TYPE = {};
  
  // PRODUCT-SPECIFIC CONFIG
  // Empty - use Metafields instead (upsell.config)
  // Example format (for reference only):
  // {
  //   'herzensfreundin-armband': {
  //     enabled: true,
  //     title: 'Vervollständige dein Set!',
  //     upsells: [
  //       {
  //         id: 'necklace',
  //         product: 'lebe-liebe-lache-circle-halskette',
  //         alternatives: ['believe-in-yourself-circle-halskette'],
  //         collection: 'circle-halsketten'
  //       }
  //     ],
  //     addons: ['geschenkverpackung-mit-schleife']
  //   }
  // }
  const UPSELL_CONFIG = {};

  const SELECTORS = {
    productForm: 'form[action*="/cart/add"]',
    addToCartButton: '[data-add-to-cart]',
    quickAddButton: '[data-quick-add-button]',
    collectionAddButton: '.quick-add-button, [data-action="add-to-cart"]'
  };

  // ===== STATE =====
  let state = {
    mainProduct: null,
    mainVariant: null,
    upsellConfig: null,
    modal: null,
    isProcessing: false,
    hasAmpCart: false,
    mainProductAddedToCart: false
  };

  // ===== INIT =====
  function init() {
    console.log('[MW Upsell] Initializing...');
    
    // Wait for Growthbook to be ready, or init immediately if not needed
    waitForGrowthbookThenInit();
  }
  
  function waitForGrowthbookThenInit() {
    // Check if Growthbook exists
    const gb = window._growthbook || window.growthbook || window.gb;
    
    if (gb && gb.ready) {
      // Growthbook is ready, check flag
      checkGrowthbookAndInit();
    } else if (gb) {
      // Growthbook exists but not ready, wait for it
      console.log('[MW Upsell] Waiting for Growthbook to be ready...');
      
      // Try to subscribe to ready event
      if (typeof gb.subscribe === 'function') {
        gb.subscribe(() => {
          if (gb.ready) {
            console.log('[MW Upsell] Growthbook is now ready!');
            checkGrowthbookAndInit();
          }
        });
      } else {
        // Fallback: wait  a bit and try again
        setTimeout(() => {
          checkGrowthbookAndInit();
        }, 500);
      }
    } else {
      // No Growthbook found, init immediately
      console.log('[MW Upsell] Growthbook not found, initializing without A/B test');
      initUpsellSystem();
    }
  }
  
  function checkGrowthbookAndInit() {
    if (!isUpsellEnabledByGrowthbook()) {
      console.log('[MW Upsell] Disabled by Growthbook A/B test');
      return; // Don't initialize if disabled by Growthbook
    }
    
    initUpsellSystem();
  }
  
  function initUpsellSystem() {
    console.log('[MW Upsell] Initializing upsell system...');
    
    // Check if AMP Cart is available
    checkForAmpCart();
    
    // Add data-product-type to product forms if missing
    addProductTypeToForms();
    
    // Intercept product form submissions
    document.addEventListener('submit', handleFormSubmit);
    
    // Intercept add-to-cart button clicks (higher priority)
    document.addEventListener('click', handleAddToCartClick, true); // Use capture phase!
    
    // Intercept collection quick-add clicks
    document.addEventListener('click', handleQuickAddClick);
    
    console.log('[MW Upsell] Ready!');
  }
  
  function addProductTypeToForms() {
    // For product pages, add product type from theme.product if available
    if (window.theme?.product?.type) {
      const forms = document.querySelectorAll('form[action*="/cart/add"]');
      
      forms.forEach(form => {
        // Add if not already present
        if (!form.dataset.productType) {
          form.dataset.productType = window.theme.product.type;
          console.log('[MW Upsell] Added product type to form from theme.product:', window.theme.product.type);
        }
      });
    }
  }
  
  function isUpsellEnabledByGrowthbook() {
    // Check if Growthbook is available
    const gb = window._growthbook || window.growthbook || window.gb;
    
    if (!gb) {
      // Growthbook not loaded yet or not available
      // Default: enabled
      console.log('[MW Upsell] Growthbook not available, defaulting to enabled');
      return true;
    }
    
    try {
      // Check feature flag: 'upsell-modal-enabled'
      // In Growthbook Admin, create feature flag with key: upsell-modal-enabled
      // Control: false (no upsell)
      // Variant: true (with upsell)
      const isEnabled = gb.getFeatureValue('upsell-modal-enabled', true); // Default true
      
      console.log('[MW Upsell] Growthbook loaded:', !!gb);
      console.log('[MW Upsell] Growthbook ready:', gb.ready);
      console.log('[MW Upsell] Feature flag "upsell-modal-enabled":', isEnabled);
      
      return isEnabled;
    } catch (error) {
      // If Growthbook has an error, default to enabled
      console.warn('[MW Upsell] Error reading Growthbook feature flag:', error);
      console.log('[MW Upsell] Defaulting to enabled due to error');
      return true;
    }
  }
  
  function checkForAmpCart() {
    // Check if AMP Cart is loaded
    if (window.AMP_API && window.AMP_API.OPEN_CART) {
      state.hasAmpCart = true;
      console.log('[MW Upsell] AMP Cart detected!');
    }
    
    // Also listen for AMP Cart loaded callback
    const originalCallback = window.AMP_CART_LOADED;
    window.AMP_CART_LOADED = function(cart) {
      state.hasAmpCart = true;
      console.log('[MW Upsell] AMP Cart loaded callback!');
      
      // Call original callback if exists
      if (originalCallback && typeof originalCallback === 'function') {
        originalCallback(cart);
      }
    };
  }

  // ===== EVENT HANDLERS =====
  async function handleAddToCartClick(e) {
    // Check if this is an add-to-cart button
    const button = e.target.closest(SELECTORS.addToCartButton);
    if (!button) return;
    
    console.log('[MW Upsell] Add-to-cart button clicked!');
    
    // Get the form
    const form = button.closest('form[action*="/cart/add"]');
    if (!form) {
      console.log('[MW Upsell] No form found for button');
      return;
    }
    
    const formData = new FormData(form);
    const variantId = formData.get('id');
    
    if (!variantId) {
      console.log('[MW Upsell] No variant ID found');
      return;
    }
    
    // Get product handle - try multiple methods
    let productHandle = getProductHandleFromForm(form);
    
    // If not found, try to get from product card (for collection pages)
    if (!productHandle) {
      productHandle = getProductHandleFromCard(button);
    }
    
    if (!productHandle) {
      console.log('[MW Upsell] No product handle found');
      return;
    }
    
    console.log('[MW Upsell] Product handle:', productHandle);
    
    // Get config - try product-specific first, then product type
    const config = await getUpsellConfig(productHandle, form);
    if (!config || !config.enabled) {
      console.log('[MW Upsell] No upsell config for this product');
      return;
    }
    
    console.log('[MW Upsell] Using config:', config.source);
    console.log('[MW Upsell] Has AMP Cart:', state.hasAmpCart);
    
    if (!state.hasAmpCart) {
      // No AMP Cart - intercept completely
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      console.log('[MW Upsell] Intercepted add-to-cart (no AMP Cart)');
      state.mainProductAddedToCart = false;
    } else {
      // Has AMP Cart - let it add the product, we'll show modal on top
      console.log('[MW Upsell] Showing modal, AMP Cart will handle add-to-cart');
      state.mainProductAddedToCart = true;
      
      // Show modal slightly delayed so AMP Cart can process first
      setTimeout(() => {
        showUpsellModal(productHandle, variantId, config);
      }, 100);
      return;
    }
    
    showUpsellModal(productHandle, variantId, config);
  }
  
  async function handleFormSubmit(e) {
    const form = e.target;
    if (!form.matches(SELECTORS.productForm)) return;
    
    const formData = new FormData(form);
    const variantId = formData.get('id');
    
    if (!variantId) return;
    
    // Get product handle from URL or form
    const productHandle = getProductHandleFromForm(form);
    if (!productHandle) return;
    
    // Get config - try product-specific first, then product type
    const config = await getUpsellConfig(productHandle, form);
    if (!config || !config.enabled) return;
    
    console.log('[MW Upsell] Using config:', config.source);
    console.log('[MW Upsell] Has AMP Cart:', state.hasAmpCart);
    
    if (!state.hasAmpCart) {
      // No AMP Cart - intercept completely
      e.preventDefault();
      e.stopPropagation();
      console.log('[MW Upsell] Intercepted form submit (no AMP Cart)');
      state.mainProductAddedToCart = false;
    } else {
      // Has AMP Cart - let form submit
      console.log('[MW Upsell] Showing modal, AMP Cart will handle form submit');
      state.mainProductAddedToCart = true;
      
      setTimeout(() => {
        showUpsellModal(productHandle, variantId, config);
      }, 100);
      return;
    }
    
    showUpsellModal(productHandle, variantId, config);
  }

  async function handleQuickAddClick(e) {
    const button = e.target.closest(SELECTORS.collectionAddButton);
    if (!button) return;
    
    // Try to get product handle from button data attributes
    const productHandle = button.dataset.productHandle || button.dataset.handle;
    const variantId = button.dataset.variantId || button.dataset.variant;
    
    if (!productHandle || !variantId) return;
    
    // Get the product card for product type
    const card = button.closest('.grid__item, .grid-product, [data-product-type]');
    
    // Get config - try product-specific first, then product type
    const config = await getUpsellConfig(productHandle, card);
    if (!config || !config.enabled) return;
    
    console.log('[MW Upsell] Using config:', config.source);
    console.log('[MW Upsell] Has AMP Cart:', state.hasAmpCart);
    
    if (!state.hasAmpCart) {
      // No AMP Cart - intercept completely
      e.preventDefault();
      e.stopPropagation();
      console.log('[MW Upsell] Intercepted quick-add (no AMP Cart)');
      state.mainProductAddedToCart = false;
    } else {
      // Has AMP Cart - let it add the product
      console.log('[MW Upsell] Showing modal, AMP Cart will handle quick-add');
      state.mainProductAddedToCart = true;
      
      setTimeout(() => {
        showUpsellModal(productHandle, variantId, config);
      }, 100);
      return;
    }
    
    showUpsellModal(productHandle, variantId, config);
  }

  // ===== MODAL =====
  async function showUpsellModal(productHandle, variantId, config) {
    if (state.isProcessing) return;
    
    state.isProcessing = true;
    state.upsellConfig = config;
    
    try {
      // Fetch ONLY main product first
      const mainProduct = await fetchProductData(productHandle);
      const mainVariant = mainProduct.variants.find(v => v.id == variantId);
      
      if (!mainVariant) {
        console.error('[MW Upsell] Variant not found');
        fallbackAddToCart(variantId);
        return;
      }
      
      state.mainProduct = mainProduct;
      state.mainVariant = mainVariant;
      
      // Show modal IMMEDIATELY with skeleton loaders
      createModalWithSkeletons(mainProduct, mainVariant, config);
      
      // Load upsells in background (async, non-blocking)
      loadUpsellsAsync(config, mainVariant);
      
    } catch (error) {
      console.error('[MW Upsell] Error loading products:', error);
      fallbackAddToCart(variantId);
    } finally {
      state.isProcessing = false;
    }
  }
  
  async function loadUpsellsAsync(config, mainVariant) {
    // Load upsells one by one
    for (let i = 0; i < config.upsells.length; i++) {
      const upsell = config.upsells[i];
      
      try {
        let upsellProduct;
        let alternativeProducts = [];
        
        // Check if main product is defined
        if (upsell.product) {
          // Load specific product
          upsellProduct = await fetchProductData(upsell.product);
        } else if (upsell.collection) {
          // No specific product - load first available from collection
          console.log('[MW Upsell] No product specified, loading from collection:', upsell.collection);
          
          const collectionUrl = `/collections/${upsell.collection}/products.json?limit=20`;
          const response = await fetch(collectionUrl);
          
          if (!response.ok) {
            throw new Error('Failed to load collection');
          }
          
          const data = await response.json();
          const products = data.products || [];
          
          // Find first product with available variant matching our color
          for (const product of products) {
            const matchedVariant = matchVariantByOption(product, mainVariant);
            const variant = matchedVariant || product.variants[0];
            
            if (variant && variant.available) {
              upsellProduct = product;
              // Use remaining products as alternatives
              alternativeProducts = products.filter(p => p.id !== product.id).slice(0, 10);
              break;
            }
          }
          
          if (!upsellProduct) {
            throw new Error('No available products in collection');
          }
        } else {
          throw new Error('No product or collection specified');
        }
        
        // Load specific alternatives (if any) - but don't block
        if (upsell.alternatives && upsell.alternatives.length > 0) {
          const altPromises = upsell.alternatives.map(handle => 
            fetchProductData(handle).catch(err => {
              console.warn('[MW Upsell] Failed to load alternative:', handle);
              return null;
            })
          );
          const loadedProducts = (await Promise.all(altPromises)).filter(p => p !== null);
          
          // Filter out products where matched variant is out of stock
          const filteredAlts = loadedProducts.filter(product => {
            const matchedVariant = matchVariantByOption(product, mainVariant);
            const variant = matchedVariant || product.variants[0];
            return variant && variant.available;
          });
          
          // Combine with collection alternatives
          alternativeProducts = [...filteredAlts, ...alternativeProducts];
        }
        
        // Replace skeleton with real content
        const isAutoSelected = !upsell.product; // True if loaded from collection only
        replaceSkeletonWithUpsell(upsell.id, upsellProduct, alternativeProducts, mainVariant, isAutoSelected);
        
      } catch (error) {
        console.error('[MW Upsell] Error loading upsell:', upsell.id, error);
        removeSkeletonSection(upsell.id);
      }
    }
    
    // Load addons last (lowest priority)
    if (config.addons && config.addons.length > 0) {
      try {
        const addonProducts = await Promise.all(
          config.addons.map(handle => fetchProductData(handle))
        );
        replaceAddonsSkeleton(addonProducts);
      } catch (error) {
        console.error('[MW Upsell] Error loading addons:', error);
      }
    }
  }

  function createModalWithSkeletons(mainProduct, mainVariant, config) {
    // Remove existing modal if any
    const existing = document.querySelector('.mw-upsell-overlay');
    if (existing) existing.remove();
    
    console.log('[MW Upsell] Creating modal with skeletons...');
    
    // Check if this is addon-only (no upsells, only addons)
    const isAddonOnly = (!config.upsells || config.upsells.length === 0) && config.addons && config.addons.length > 0;
    
    // Create modal HTML with skeletons
    const modal = document.createElement('div');
    modal.className = 'mw-upsell-overlay';
    
    if (isAddonOnly) {
      // ADDON-ONLY LAYOUT (Clean, no checkbox)
      modal.innerHTML = `
        <div class="mw-upsell-modal mw-upsell-modal--addon-only">
          <div class="mw-upsell-header">
            <h3>${config.title || 'Mach es besonders! 🎁'}</h3>
            <button class="mw-upsell-close" data-action="close">&times;</button>
          </div>
          
          <div class="mw-upsell-content">
            <!-- Main Product -->
            <div class="mw-upsell-main-product">
              <img src="${resizeImage(getVariantImage(mainProduct, mainVariant), 80)}" alt="${mainProduct.title}">
              <div class="mw-upsell-main-info">
                <h4>${mainProduct.title}</h4>
                <p>${mainVariant.title} · ${formatPrice(mainVariant.price)}</p>
              </div>
            </div>
            
            <div class="mw-upsell-divider"></div>
            
            <!-- Addon Skeleton (centered, no checkbox) -->
            <div class="mw-upsell-addon-only-section" data-addons-skeleton>
              <div class="mw-upsell-skeleton-card mw-upsell-skeleton-card--centered">
                <div class="mw-upsell-skeleton-image large"></div>
                <div class="mw-upsell-skeleton-text">
                  <div class="mw-upsell-skeleton-line"></div>
                  <div class="mw-upsell-skeleton-line short"></div>
                </div>
              </div>
            </div>
          </div>
          
          <div class="mw-upsell-footer">
            <button class="mw-upsell-cta" data-action="add-addon">
              <span data-addon-cta-text>Wird geladen...</span>
            </button>
            <button class="mw-upsell-skip" data-action="skip">
              Nein danke, nur das Produkt
            </button>
          </div>
        </div>
      `;
    } else {
      // NORMAL LAYOUT (with upsells and checkboxes)
      modal.innerHTML = `
        <div class="mw-upsell-modal">
          <div class="mw-upsell-header">
            <h3>${config.title}</h3>
            <button class="mw-upsell-close" data-action="close">&times;</button>
          </div>
          
          <div class="mw-upsell-content">
            <!-- Main Product -->
            <div class="mw-upsell-main-product">
              <img src="${resizeImage(getVariantImage(mainProduct, mainVariant), 80)}" alt="${mainProduct.title}">
              <div class="mw-upsell-main-info">
                <h4>${mainProduct.title}</h4>
                <p>${mainVariant.title} · ${formatPrice(mainVariant.price)}</p>
              </div>
            </div>
            
            <div class="mw-upsell-divider"></div>
            
            <!-- Upsells Header -->
            <div class="mw-upsell-header-section">
              <h4>Perfekt dazu:</h4>
            </div>
            
            <!-- Upsell Skeletons -->
            ${config.upsells.map(upsell => `
              <div class="mw-upsell-section mw-upsell-skeleton" data-upsell-skeleton="${upsell.id}">
                <div class="mw-upsell-skeleton-card">
                  <div class="mw-upsell-skeleton-checkbox"></div>
                  <div class="mw-upsell-skeleton-image"></div>
                  <div class="mw-upsell-skeleton-text">
                    <div class="mw-upsell-skeleton-line"></div>
                    <div class="mw-upsell-skeleton-line short"></div>
                  </div>
                </div>
              </div>
            `).join('')}
            
            <!-- Addons Skeleton -->
            ${config.addons && config.addons.length > 0 ? `
              <div class="mw-upsell-section mw-upsell-skeleton" data-addons-skeleton>
                <div class="mw-upsell-skeleton-card">
                  <div class="mw-upsell-skeleton-checkbox"></div>
                  <div class="mw-upsell-skeleton-image small"></div>
                  <div class="mw-upsell-skeleton-text">
                    <div class="mw-upsell-skeleton-line short"></div>
                  </div>
                </div>
              </div>
            ` : ''}
          </div>
          
          <div class="mw-upsell-footer">
            <button class="mw-upsell-cta" data-action="add-to-cart">
              In den Warenkorb
            </button>
            <button class="mw-upsell-skip" data-action="skip">
              Nein danke, nur ${mainProduct.title}
            </button>
          </div>
        </div>
      `;
    }
    
    document.body.appendChild(modal);
    state.modal = modal;
    
    // Bind events
    bindModalEvents(modal);
    
    // Show modal
    setTimeout(() => modal.classList.add('active'), 10);
  }

  function replaceSkeletonWithUpsell(upsellId, upsellProduct, alternativeProducts, mainVariant, isAutoSelected = false) {
    const skeleton = state.modal.querySelector(`[data-upsell-skeleton="${upsellId}"]`);
    if (!skeleton) return;
    
    const upsellConfig = state.upsellConfig.upsells.find(u => u.id === upsellId);
    if (!upsellConfig) return;
    
    // Create real content
    const content = renderUpsellSection({ 
      config: upsellConfig, 
      product: upsellProduct, 
      alternatives: alternativeProducts,
      isAutoSelected: isAutoSelected
    }, mainVariant);
    
    // Replace skeleton with real content
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = content;
    const newContent = tempDiv.firstElementChild;
    
    skeleton.replaceWith(...tempDiv.children);
    
    // Bind events for this section
    bindUpsellSectionEvents(upsellId);
  }
  
  function replaceAddonsSkeleton(addonProducts) {
    const skeleton = state.modal.querySelector('[data-addons-skeleton]');
    if (!skeleton || !addonProducts.length) return;
    
    // Check if this is addon-only layout
    const isAddonOnly = state.modal.querySelector('.mw-upsell-modal--addon-only');
    
    if (isAddonOnly && addonProducts.length === 1) {
      // ADDON-ONLY LAYOUT (Clean, centered, no checkbox)
      const addon = addonProducts[0];
      const content = `
        <div class="mw-upsell-addon-only-section">
          <div class="mw-upsell-addon-only-card" data-addon-product="${addon.handle}" data-addon-variant="${addon.variants[0].id}">
            <img src="${resizeImage(getVariantImage(addon, addon.variants[0]), 200)}" alt="${addon.title}" class="mw-upsell-addon-only-image">
            <h5 class="mw-upsell-addon-only-title">${addon.title}</h5>
            <p class="mw-upsell-addon-only-price">${formatPrice(addon.variants[0].price)}</p>
            ${addon.description ? `<p class="mw-upsell-addon-only-desc">${addon.description}</p>` : ''}
          </div>
        </div>
      `;
      
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = content;
      skeleton.replaceWith(...tempDiv.children);
      
      // Update the CTA button text
      const ctaText = state.modal.querySelector('[data-addon-cta-text]');
      if (ctaText) {
        ctaText.textContent = `🎁 Mit Geschenkbox hinzufügen (+${formatPrice(addon.variants[0].price)})`;
      }
      
    } else {
      // NORMAL LAYOUT (with checkboxes)
      const content = `
        <div class="mw-upsell-section">
          ${addonProducts.map(addon => `
            <label class="mw-upsell-product-add">
              <input type="checkbox" class="mw-upsell-product-checkbox" data-addon-product="${addon.handle}" data-addon-variant="${addon.variants[0].id}">
              <div class="mw-upsell-product-card mw-upsell-addon-card">
                <img src="${resizeImage(getVariantImage(addon, addon.variants[0]), 80)}" alt="${addon.title}" class="mw-upsell-product-image">
                <div class="mw-upsell-product-info">
                  <h5>${addon.title}</h5>
                  <p class="mw-upsell-product-subtitle">${formatPrice(addon.variants[0].price)}</p>
                </div>
              </div>
            </label>
          `).join('')}
        </div>
      `;
      
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = content;
      skeleton.replaceWith(...tempDiv.children);
    }
  }
  
  function removeSkeletonSection(upsellId) {
    const skeleton = state.modal.querySelector(`[data-upsell-skeleton="${upsellId}"]`);
    if (skeleton) {
      const prevDivider = skeleton.previousElementSibling;
      if (prevDivider && prevDivider.classList.contains('mw-upsell-divider')) {
        prevDivider.remove();
      }
      skeleton.remove();
    }
  }
  
  function bindUpsellSectionEvents(upsellId) {
    if (!state.modal) return;
    
    const section = state.modal.querySelector(`[data-upsell-id="${upsellId}"]`);
    if (!section) return;
    
    // Toggle gravur picker (both open and close buttons)
    const toggleButtons = section.querySelectorAll(`[data-toggle-gravur="${upsellId}"]`);
    const gravurPicker = section.querySelector(`[data-gravur-picker="${upsellId}"]`);
    
    toggleButtons.forEach(toggleButton => {
      toggleButton.addEventListener('click', () => {
        const isActive = gravurPicker.classList.toggle('active');
        
        // Update the main toggle button text (not the close X button)
        const mainToggle = section.querySelector(`button.mw-upsell-change-gravur[data-toggle-gravur="${upsellId}"]`);
        if (mainToggle && mainToggle === toggleButton) {
          // This is the main button, not the close button
          // Don't need to update text as it's replaced by close button
        }
      });
    });
    
    // Radio buttons for alternative products - update card when changed
    const productRadios = section.querySelectorAll(`input[name="upsell-product-${upsellId}"]`);
    productRadios.forEach(radio => {
      radio.addEventListener('change', async (e) => {
        if (e.target.checked) {
          await updateUpsellCard(upsellId, e.target);
        }
      });
    });
    
    // Zoom buttons
    const zoomButtons = section.querySelectorAll('[data-zoom-image]');
    zoomButtons.forEach(button => {
      button.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        showImageZoom(button.dataset.zoomImage, button.dataset.zoomTitle);
      });
    });
    
    // Load more button
    const loadMoreButton = section.querySelector('[data-load-more-gravurs]');
    if (loadMoreButton) {
      loadMoreButton.addEventListener('click', async () => {
        await handleLoadMoreGravurs(loadMoreButton);
      });
    }
  }
  
  async function updateUpsellCard(upsellId, selectedRadio) {
    const section = state.modal.querySelector(`[data-upsell-id="${upsellId}"]`);
    if (!section) return;
    
    const variantId = selectedRadio.value;
    const productHandle = selectedRadio.dataset.productHandle;
    
    console.log('[MW Upsell] Updating card for:', productHandle, variantId);
    
    // Get the product data
    try {
      const product = await fetchProductData(productHandle);
      const variant = product.variants.find(v => v.id == variantId);
      
      if (!variant) return;
      
      // Update the checkbox card
      const checkbox = section.querySelector('[data-upsell-checkbox]');
      const card = section.querySelector('.mw-upsell-product-card');
      
      if (checkbox && card) {
        // Add updating animation
        card.style.opacity = '0.5';
        
        // Update variant ID
        checkbox.dataset.matchedVariantId = variant.id;
        
        // Update image
        const img = card.querySelector('.mw-upsell-product-image');
        if (img) {
          img.src = resizeImage(getVariantImage(product, variant), 80);
          img.alt = product.title;
        }
        
        // Update title
        const title = card.querySelector('h5');
        if (title) {
          title.textContent = product.title;
        }
        
        // Update subtitle (when user selects a different gravur)
        const subtitle = card.querySelector('.mw-upsell-product-subtitle');
        if (subtitle) {
          // When user actively selects, it's NOT the same gravur anymore
          subtitle.textContent = `${variant.title} · ${formatPrice(variant.price)}`;
        }
        
        // Remove animation
        setTimeout(() => {
          card.style.opacity = '1';
        }, 200);
      }
      
    } catch (error) {
      console.error('[MW Upsell] Error updating card:', error);
    }
  }
  
  function renderUpsellSection(upsellData, mainVariant) {
    const { config: upsellConfig, product: upsellProduct, alternatives: alternativeProducts, isAutoSelected } = upsellData;
    
    // Match upsell variant to main product variant (by color)
    const matchedUpsellVariant = matchVariantByOption(upsellProduct, mainVariant) || upsellProduct.variants[0];
    
    // Check if variant is available
    if (!matchedUpsellVariant.available) {
      console.log('[MW Upsell] Skipping out of stock:', upsellProduct.title);
      return ''; // Skip this upsell if out of stock
    }
    
    console.log('[MW Upsell]', upsellProduct.title, '- Matched variant:', matchedUpsellVariant.title);
    
    // Put the main product FIRST in alternatives list, then the rest
    const allAlternatives = [
      { ...upsellProduct, isCurrent: true }, // Mark as current so we can style it
      ...alternativeProducts
    ];
    
    // Determine subtitle text
    const subtitleText = isAutoSelected 
      ? `${matchedUpsellVariant.title} · ${formatPrice(matchedUpsellVariant.price)}`
      : `Gleiche Gravur · ${matchedUpsellVariant.title} · ${formatPrice(matchedUpsellVariant.price)}`;
    
    return `
      <div class="mw-upsell-section" data-upsell-id="${upsellConfig.id}">
        <label class="mw-upsell-product-add">
          <input 
            type="checkbox" 
            class="mw-upsell-product-checkbox" 
            data-upsell-checkbox 
            data-upsell-id="${upsellConfig.id}"
            checked
            data-matched-variant-id="${matchedUpsellVariant.id}"
            data-product-handle="${upsellProduct.handle}"
          >
          <div class="mw-upsell-product-card">
            <img src="${resizeImage(getVariantImage(upsellProduct, matchedUpsellVariant), 80)}" alt="${upsellProduct.title}" class="mw-upsell-product-image">
            <div class="mw-upsell-product-info">
              <h5>${upsellProduct.title}</h5>
              <p class="mw-upsell-product-subtitle">${subtitleText}</p>
              ${allAlternatives.length > 1 || upsellConfig.collection ? `
                <button type="button" class="mw-upsell-change-link" data-toggle-gravur="${upsellConfig.id}">
                  Andere Gravur wählen
                </button>
              ` : ''}
            </div>
          </div>
        </label>
        
        ${allAlternatives.length > 1 || upsellConfig.collection ? `
          
          <!-- Gravur Picker (hidden initially) -->
          <div class="mw-upsell-gravur-picker" data-gravur-picker="${upsellConfig.id}">
            <div class="mw-upsell-gravur-header">
              <p class="mw-upsell-gravur-title">Wähle eine Gravur:</p>
              <button type="button" class="mw-upsell-gravur-close" data-toggle-gravur="${upsellConfig.id}">✕</button>
            </div>
            <div class="mw-upsell-gravur-grid" data-gravur-grid="${upsellConfig.id}">
              ${renderAlternativeProducts(allAlternatives, mainVariant, upsellConfig.id)}
            </div>
            ${upsellConfig.collection ? `
              <button type="button" class="mw-upsell-show-more" data-load-more-gravurs data-collection="${upsellConfig.collection}" data-upsell-id="${upsellConfig.id}" data-page="1">
                Mehr Gravuren anzeigen
              </button>
            ` : ''}
          </div>
        ` : ''}
      </div>
    `;
  }
  
  function renderAlternativeProducts(products, referenceVariant, upsellId) {
    // Show different products (different engravings), not variants
    // Filter out products where matched variant is out of stock
    let validIndex = 0;
    
    return products.map((product, index) => {
      // Match variant by color/option from reference variant
      const matchedVariant = matchVariantByOption(product, referenceVariant);
      const variant = matchedVariant || product.variants[0];
      
      // Skip if out of stock
      if (!variant.available) {
        console.log('[MW Upsell] Skipping out of stock alternative:', product.title, variant.title);
        return '';
      }
      
      const imageUrl = getVariantImage(product, variant);
      const isCurrent = product.isCurrent; // First item is the currently selected one
      const isChecked = validIndex === 0; // First AVAILABLE item is checked
      
      validIndex++;
      
      return `
        <div class="mw-upsell-gravur-item">
          <input 
            type="radio" 
            name="upsell-product-${upsellId}" 
            value="${variant.id}" 
            data-product-handle="${product.handle}"
            data-upsell-id="${upsellId}"
            id="alt-product-${upsellId}-${product.id}"
            ${isChecked ? 'checked' : ''}
          >
          <label for="alt-product-${upsellId}-${product.id}" class="mw-upsell-gravur-label">
            <div class="mw-upsell-gravur-img-wrapper">
              <img 
                src="${resizeImage(imageUrl, 300)}" 
                alt="${product.title}"
                class="mw-upsell-gravur-img"
              >
              <button 
                type="button"
                class="mw-upsell-gravur-zoom" 
                data-zoom-image="${imageUrl}"
                data-zoom-title="${product.title}"
                aria-label="Bild vergrößern"
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <circle cx="7" cy="7" r="5.5" stroke="white" stroke-width="1.5"/>
                  <path d="M11 11L14 14" stroke="white" stroke-width="1.5" stroke-linecap="round"/>
                  <path d="M7 5V9M5 7H9" stroke="white" stroke-width="1.5" stroke-linecap="round"/>
                </svg>
              </button>
            </div>
            <div class="mw-upsell-gravur-info">
              <div class="mw-upsell-gravur-name">${product.title}</div>
              <div class="mw-upsell-gravur-price">${formatPrice(variant.price)}</div>
            </div>
          </label>
        </div>
      `;
    }).filter(html => html !== '').join('');
  }
  
  function renderGravurOptions(product) {
    // Deprecated - kept for backwards compatibility
    // Show first 4 variants
    const variants = product.variants.slice(0, 4);
    
    return variants.map((variant, index) => `
      <div class="mw-upsell-gravur-item">
        <input 
          type="radio" 
          name="upsell-variant" 
          value="${variant.id}" 
          id="variant-${variant.id}"
          ${index === 0 ? 'checked' : ''}
        >
        <label for="variant-${variant.id}" class="mw-upsell-gravur-label">
          <img 
            src="${getVariantImage(product, variant)}" 
            alt="${variant.title}"
            class="mw-upsell-gravur-img"
          >
          <div class="mw-upsell-gravur-name">${variant.title}</div>
        </label>
      </div>
    `).join('');
  }

  function bindModalEvents(modal) {
    // Close button
    modal.querySelector('[data-action="close"]').addEventListener('click', closeModal);
    
    // Click outside to close
    modal.addEventListener('click', (e) => {
      if (e.target === modal) closeModal();
    });
    
    // Add to cart button (normal flow)
    const addToCartBtn = modal.querySelector('[data-action="add-to-cart"]');
    if (addToCartBtn) {
      addToCartBtn.addEventListener('click', handleAddToCart);
    }
    
    // Add addon button (addon-only flow)
    const addAddonBtn = modal.querySelector('[data-action="add-addon"]');
    if (addAddonBtn) {
      addAddonBtn.addEventListener('click', handleAddAddon);
    }
    
    // Skip button
    modal.querySelector('[data-action="skip"]').addEventListener('click', handleSkip);
  }

  function closeModal() {
    if (state.modal) {
      state.modal.classList.remove('active');
      setTimeout(() => {
        state.modal.remove();
        state.modal = null;
      }, 300);
    }
  }
  
  function showImageZoom(imageUrl, title) {
    // Create zoom overlay
    const zoomOverlay = document.createElement('div');
    zoomOverlay.className = 'mw-upsell-zoom-overlay';
    zoomOverlay.innerHTML = `
      <div class="mw-upsell-zoom-content">
        <button class="mw-upsell-zoom-close" aria-label="Schließen">&times;</button>
        <img src="${imageUrl}" alt="${title}" class="mw-upsell-zoom-img">
        <p class="mw-upsell-zoom-title">${title}</p>
      </div>
    `;
    
    document.body.appendChild(zoomOverlay);
    
    // Close on click
    zoomOverlay.addEventListener('click', (e) => {
      if (e.target === zoomOverlay || e.target.classList.contains('mw-upsell-zoom-close')) {
        zoomOverlay.classList.remove('active');
        setTimeout(() => zoomOverlay.remove(), 300);
      }
    });
    
    // Show with animation
    setTimeout(() => zoomOverlay.classList.add('active'), 10);
  }
  
  async function handleLoadMoreGravurs(button) {
    const collectionHandle = button.dataset.collection;
    const upsellId = button.dataset.upsellId;
    const currentPage = parseInt(button.dataset.page) || 1;
    const grid = state.modal.querySelector(`[data-gravur-grid="${upsellId}"]`);
    const originalText = button.textContent;
    
    if (!collectionHandle) {
      console.error('[MW Upsell] No collection configured');
      button.textContent = 'Keine Collection konfiguriert';
      button.disabled = true;
      return;
    }
    
    // Get already shown product handles
    const shownHandles = new Set();
    state.modal.querySelectorAll('[data-product-handle]').forEach(input => {
      shownHandles.add(input.dataset.productHandle);
    });
    
    button.disabled = true;
    button.textContent = 'Lädt...';
    
    try {
      console.log('[MW Upsell] Loading products from collection:', collectionHandle, 'page:', currentPage);
      
      // Load collection products via Shopify's JSON endpoint
      // You can paginate: ?page=1, ?page=2, etc.
      const collectionUrl = `/collections/${collectionHandle}/products.json?limit=50&page=${currentPage}`;
      
      const response = await fetch(collectionUrl);
      if (!response.ok) {
        throw new Error('Collection load failed');
      }
      
      const data = await response.json();
      const products = data.products || [];
      
      console.log('[MW Upsell] Found products in collection:', products.length);
      
      // Filter out already shown products AND out of stock products
      const newProducts = products.filter(product => {
        if (shownHandles.has(product.handle)) return false;
        
        // Check if any variant matching our color is available
        const matchedVariant = matchVariantByOption(product, state.mainVariant);
        const variant = matchedVariant || product.variants[0];
        
        return variant && variant.available;
      });
      
      console.log('[MW Upsell] New products (in stock):', newProducts.length);
      
      if (newProducts.length === 0) {
        // Try next page if we haven't loaded anything
        if (products.length > 0) {
          button.dataset.page = currentPage + 1;
          button.textContent = originalText;
          button.disabled = false;
          await handleLoadMoreGravurs(button);
          return;
        }
        
        button.textContent = 'Alle Gravuren geladen';
        button.disabled = true;
        return;
      }
      
      // Take up to 10 new products
      const productsToShow = newProducts.slice(0, 10);
      
      // Render and append new products
      const newHTML = renderAlternativeProducts(productsToShow, state.mainVariant, upsellId);
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = newHTML;
      
      // Append each item to grid
      Array.from(tempDiv.children).forEach(child => {
        grid.appendChild(child);
      });
      
      // Re-bind zoom buttons for new items
      const newZoomButtons = grid.querySelectorAll('[data-zoom-image]');
      newZoomButtons.forEach(btn => {
        if (!btn.hasAttribute('data-zoom-bound')) {
          btn.setAttribute('data-zoom-bound', 'true');
          btn.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            showImageZoom(btn.dataset.zoomImage, btn.dataset.zoomTitle);
          });
        }
      });
      
      // Re-bind radio button change events for new items
      const newRadios = grid.querySelectorAll(`input[name="upsell-product-${upsellId}"]`);
      newRadios.forEach(radio => {
        if (!radio.hasAttribute('data-change-bound')) {
          radio.setAttribute('data-change-bound', 'true');
          radio.addEventListener('change', async (e) => {
            if (e.target.checked) {
              await updateUpsellCard(upsellId, e.target);
            }
          });
        }
      });
      
      // Update page counter for next load
      if (newProducts.length > 10 || products.length === 50) {
        // There might be more on this page or next page
        button.dataset.page = currentPage + 1;
        button.textContent = 'Noch mehr anzeigen';
        button.disabled = false;
      } else {
        button.textContent = 'Alle Gravuren geladen';
        button.disabled = true;
      }
      
    } catch (error) {
      console.error('[MW Upsell] Error loading collection:', error);
      button.textContent = originalText;
      button.disabled = false;
    }
  }

  // ===== ADD TO CART LOGIC =====
  async function handleAddToCart() {
    const button = state.modal.querySelector('[data-action="add-to-cart"]');
    const originalText = button.textContent;
    
    button.disabled = true;
    button.innerHTML = 'Wird hinzugefügt... <span class="mw-upsell-loading"></span>';
    
    try {
      const items = [];
      
      // Add main product ONLY if it wasn't already added by AMP Cart
      if (!state.mainProductAddedToCart) {
        items.push({
          id: state.mainVariant.id,
          quantity: 1,
          properties: {
            _upsell_flow: 'main_product'
          }
        });
      } else {
        console.log('[MW Upsell] Main product already in cart via AMP Cart, only adding upsells');
      }
      
      // Check all upsell categories
      const upsellCheckboxes = state.modal.querySelectorAll('[data-upsell-checkbox]');
      
      for (const checkbox of upsellCheckboxes) {
        if (!checkbox.checked) continue;
        
        const upsellId = checkbox.dataset.upsellId;
        const gravurPicker = state.modal.querySelector(`[data-gravur-picker="${upsellId}"]`);
        const isPickerActive = gravurPicker && gravurPicker.classList.contains('active');
        
        if (isPickerActive) {
          // Add selected alternative product (different gravur)
          const selectedProduct = state.modal.querySelector(`input[name="upsell-product-${upsellId}"]:checked`);
          if (selectedProduct) {
            items.push({
              id: parseInt(selectedProduct.value),
              quantity: 1,
              properties: {
                _upsell_flow: 'upsell',
                _upsell_type: upsellId,
                _upsell_selection: 'alternative_gravur'
              }
            });
          }
        } else {
          // Add upsell with same variant (already matched on modal creation)
          const matchedVariantId = checkbox.dataset.matchedVariantId;
          
          if (matchedVariantId) {
            items.push({
              id: parseInt(matchedVariantId),
              quantity: 1,
              properties: {
                _upsell_flow: 'upsell',
                _upsell_type: upsellId,
                _upsell_selection: 'same_gravur'
              }
            });
          }
        }
      }
      
      // Check addons
      const addonCheckboxes = state.modal.querySelectorAll('[data-addon-product]:checked');
      for (const checkbox of addonCheckboxes) {
        const variantId = checkbox.dataset.addonVariant;
        const addonHandle = checkbox.dataset.addonProduct;
        
        if (variantId) {
          items.push({
            id: parseInt(variantId),
            quantity: 1,
            properties: {
              _upsell_flow: 'addon',
              _addon_type: addonHandle
            }
          });
        }
      }
      
      // Add upsell items to cart (if any)
      if (items.length > 0) {
        console.log('[MW Upsell] Adding items with properties:', items);
        await addItemsToCart(items);
        
        // AMP Cart will automatically update via AMP_CART_UPDATED callback
        // when we use /cart/add.js - no need for manual refresh!
      }
      
      // Close modal
      closeModal();
      
      // Open cart (or it's already open if AMP Cart)
      if (!state.hasAmpCart) {
        openCart();
      } else {
        // AMP Cart is already open and will show the updated items
        console.log('[MW Upsell] AMP Cart should automatically update with new items');
      }
      
    } catch (error) {
      console.error('[MW Upsell] Error adding to cart:', error);
      alert('Es gab einen Fehler. Bitte versuche es erneut.');
      button.disabled = false;
      button.textContent = originalText;
    }
  }

  async function handleSkip() {
    const button = state.modal.querySelector('[data-action="skip"]');
    button.disabled = true;
    
    try {
      // Add only main product (if not already added by AMP Cart)
      if (!state.mainProductAddedToCart) {
        await addItemsToCart([{
          id: state.mainVariant.id,
          quantity: 1,
          properties: {
            _upsell_flow: 'declined'
          }
        }]);
      }
      
      closeModal();
      
      // Open cart (or it's already open if AMP Cart)
      if (!state.hasAmpCart) {
        openCart();
      } else {
        console.log('[MW Upsell] Skipped upsells, AMP Cart already open');
      }
      
    } catch (error) {
      console.error('[MW Upsell] Error adding to cart:', error);
      alert('Es gab einen Fehler. Bitte versuche es erneut.');
      button.disabled = false;
    }
  }

  async function handleAddAddon() {
    const button = state.modal.querySelector('[data-action="add-addon"]');
    const originalHTML = button.innerHTML;
    
    button.disabled = true;
    button.innerHTML = 'Wird hinzugefügt... <span class="mw-upsell-loading"></span>';
    
    try {
      const items = [];
      
      // Add main product ONLY if it wasn't already added by AMP Cart
      if (!state.mainProductAddedToCart) {
        items.push({
          id: state.mainVariant.id,
          quantity: 1,
          properties: {
            _upsell_flow: 'main_product'
          }
        });
      }
      
      // Get addon data from the card
      const addonCard = state.modal.querySelector('[data-addon-product]');
      if (addonCard) {
        const variantId = addonCard.dataset.addonVariant;
        const addonHandle = addonCard.dataset.addonProduct;
        
        if (variantId) {
          items.push({
            id: parseInt(variantId),
            quantity: 1,
            properties: {
              _upsell_flow: 'addon',
              _addon_type: addonHandle
            }
          });
        }
      }
      
      // Add items to cart
      if (items.length > 0) {
        console.log('[MW Upsell] Adding items (addon-only flow):', items);
        await addItemsToCart(items);
      }
      
      // Close modal
      closeModal();
      
      // Open cart (or it's already open if AMP Cart)
      if (!state.hasAmpCart) {
        openCart();
      } else {
        console.log('[MW Upsell] AMP Cart should automatically update with new items');
      }
      
    } catch (error) {
      console.error('[MW Upsell] Error adding to cart:', error);
      alert('Es gab einen Fehler. Bitte versuche es erneut.');
      button.disabled = false;
      button.innerHTML = originalHTML;
    }
  }

  // ===== SHOPIFY API =====
  async function fetchProductData(handle) {
    const response = await fetch(`/products/${handle}.js`);
    if (!response.ok) throw new Error(`Failed to fetch product: ${handle}`);
    return response.json();
  }

  async function addItemsToCart(items) {
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ items })
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.description || 'Failed to add to cart');
    }
    
    return response.json();
  }

  function fallbackAddToCart(variantId) {
    // If upsell fails, add normally
    fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        items: [{ id: variantId, quantity: 1 }]
      })
    }).then(() => {
      openCart();
    });
  }
  
  function openCart() {
    console.log('[MW Upsell] Opening cart...');
    
    // Try AMP Cart first
    if (window.AMP_API && typeof window.AMP_API.OPEN_CART === 'function') {
      console.log('[MW Upsell] Opening AMP cart');
      window.AMP_API.OPEN_CART();
      return;
    }
    
    // Try theme drawer cart
    if (typeof theme !== 'undefined' && theme.cart && typeof theme.cart.openCart === 'function') {
      console.log('[MW Upsell] Opening theme drawer cart');
      theme.cart.openCart();
      return;
    }
    
    // Trigger cart open event (some themes listen to this)
    document.dispatchEvent(new CustomEvent('cart:open'));
    
    // Fallback: redirect to cart page after a short delay
    setTimeout(() => {
      console.log('[MW Upsell] Redirecting to cart page (fallback)');
      window.location.href = '/cart';
    }, 500);
  }

  // ===== CONFIG HELPERS =====
  async function getUpsellConfig(productHandle, formOrCard) {
    // Check if upsell system is globally enabled
    if (window.theme?.upsell?.enabled === false) {
      console.log('[MW Upsell] System disabled in theme settings');
      return null;
    }
    
    // 1. Try hardcoded product-specific config first (for backwards compatibility)
    if (UPSELL_CONFIG[productHandle]) {
      console.log('[MW Upsell] Using hardcoded product-specific config for:', productHandle);
      return { ...UPSELL_CONFIG[productHandle], source: 'hardcoded-product' };
    }
    
    // 2. Try to get metafields from DOM
    let productType = null;
    let metafieldEnabled = null;
    let metafieldConfig = null;
    
    if (formOrCard) {
      // Try to get from grid item data attributes
      const gridItem = formOrCard.closest ? formOrCard.closest('.grid__item[data-product-type]') : null;
      
      if (gridItem) {
        productType = gridItem.dataset?.productType;
        metafieldEnabled = gridItem.dataset?.upsellEnabled;
        metafieldConfig = gridItem.dataset?.upsellConfig;
      }
      
      // Also try direct on element
      if (!productType) productType = formOrCard.dataset?.productType;
      if (!metafieldEnabled) metafieldEnabled = formOrCard.dataset?.upsellEnabled;
      if (!metafieldConfig) metafieldConfig = formOrCard.dataset?.upsellConfig;
    }
    
    console.log('[MW Upsell] Product type from DOM:', productType);
    console.log('[MW Upsell] Metafield enabled:', metafieldEnabled);
    console.log('[MW Upsell] Metafield config:', metafieldConfig);
    
    // If product has upsell.enabled = false metafield, skip
    if (metafieldEnabled === 'false') {
      console.log('[MW Upsell] Upsell disabled via metafield');
      return null;
    }
    
    // If product has complete JSON config in metafield, use that
    if (metafieldConfig && metafieldConfig !== 'undefined') {
      try {
        const parsedConfig = JSON.parse(metafieldConfig);
        console.log('[MW Upsell] Using metafield config');
        return { ...parsedConfig, enabled: true, source: 'metafield-config' };
      } catch (error) {
        console.warn('[MW Upsell] Could not parse metafield config:', error);
      }
    }
    
    // 3. If no product type yet, fetch product data
    if (!productType) {
      console.log('[MW Upsell] Fetching product data to get product_type...');
      try {
        const productData = await fetchProductData(productHandle);
        productType = productData.product_type;
      } catch (error) {
        console.warn('[MW Upsell] Could not fetch product data:', error);
        return null;
      }
    }
    
    // 4. Try hardcoded global config by type
    if (productType && GLOBAL_CONFIG_BY_TYPE[productType]) {
      console.log('[MW Upsell] Using hardcoded global config for product type:', productType);
      return { ...GLOBAL_CONFIG_BY_TYPE[productType], source: 'hardcoded-global' };
    }
    
    // 5. Try theme settings global config
    if (window.theme?.upsell?.globalConfig && productType) {
      try {
        const globalConfig = typeof window.theme.upsell.globalConfig === 'string' 
          ? JSON.parse(window.theme.upsell.globalConfig)
          : window.theme.upsell.globalConfig;
        
        if (globalConfig[productType]) {
          console.log('[MW Upsell] Using theme settings config for product type:', productType);
          const typeConfig = globalConfig[productType];
          
          // Build upsells from collections
          const collections = Array.isArray(typeConfig.collections) 
            ? typeConfig.collections 
            : (typeConfig.collections ? [typeConfig.collections] : []);
          
          // Filter out empty strings
          const validCollections = collections.filter(c => c && c.trim() !== '');
          
          const upsells = validCollections.map((collection, index) => ({
            id: `upsell_${index}`,
            title: typeConfig.titles?.[index] || 'Passend dazu?',
            collection: collection
          }));
          
          // Get addons
          const addons = window.theme.upsell.defaultAddon ? [window.theme.upsell.defaultAddon] : [];
          
          // If no upsells but has addons, use addon-only title
          const title = (upsells.length === 0 && addons.length > 0) 
            ? 'Mach es besonders! 🎁'
            : (typeConfig.title || 'Vervollständige dein Set!');
          
          return {
            enabled: true,
            title: title,
            upsells: upsells,
            addons: addons,
            source: 'theme-settings'
          };
        }
      } catch (error) {
        console.warn('[MW Upsell] Error parsing theme settings config:', error);
      }
    }
    
    // 6. No config found - modal will not show
    console.log('[MW Upsell] No config found for product type:', productType);
    return null;
  }
  
  // ===== HELPERS =====
  function getProductHandleFromCard(button) {
    console.log('[MW Upsell] Trying to get handle from product card...');
    
    // Find the product card container (SimplePledge uses .grid__item with double underscore!)
    const card = button.closest('.grid__item, .grid-product, .product-card, .grid-item, .product-item, [data-product-handle], [data-product]');
    
    if (!card) {
      console.log('[MW Upsell] No product card found');
      return null;
    }
    
    // Try data attributes
    if (card.dataset.productHandle) {
      console.log('[MW Upsell] Found handle from card data-product-handle:', card.dataset.productHandle);
      return card.dataset.productHandle;
    }
    
    if (card.dataset.product) {
      console.log('[MW Upsell] Found handle from card data-product:', card.dataset.product);
      return card.dataset.product;
    }
    
    // Try to find product link in the card
    const productLink = card.querySelector('a[href*="/products/"]');
    if (productLink) {
      const href = productLink.getAttribute('href');
      console.log('[MW Upsell] Found product link:', href);
      
      const match = href.match(/\/products\/([^\/\?]+)/);
      if (match) {
        console.log('[MW Upsell] Extracted handle from link:', match[1]);
        return match[1];
      }
    }
    
    // Try form data attributes
    const form = button.closest('form');
    if (form) {
      if (form.dataset.productHandle) {
        console.log('[MW Upsell] Found handle from form data-product-handle:', form.dataset.productHandle);
        return form.dataset.productHandle;
      }
      
      if (form.dataset.product) {
        console.log('[MW Upsell] Found handle from form data-product:', form.dataset.product);
        return form.dataset.product;
      }
    }
    
    console.log('[MW Upsell] Could not extract handle from card');
    return null;
  }
  
  function getProductHandleFromForm(form) {
    // Try to get from URL (handles /products/handle and /collections/x/products/handle)
    const url = window.location.pathname;
    console.log('[MW Upsell] Current URL:', url);
    
    const match = url.match(/\/products\/([^\/\?]+)/);
    if (match) {
      console.log('[MW Upsell] Found handle from URL:', match[1]);
      return match[1];
    }
    
    // Try from form action
    const action = form.action;
    if (action) {
      console.log('[MW Upsell] Form action:', action);
      const actionMatch = action.match(/products\/([^\/\?]+)/);
      if (actionMatch) {
        console.log('[MW Upsell] Found handle from form action:', actionMatch[1]);
        return actionMatch[1];
      }
    }
    
    // Try from form data attributes
    if (form.dataset.productHandle) {
      console.log('[MW Upsell] Found handle from data attribute:', form.dataset.productHandle);
      return form.dataset.productHandle;
    }
    
    // Try from hidden input
    const handleInput = form.querySelector('input[name="product-handle"], input[name="handle"]');
    if (handleInput && handleInput.value) {
      console.log('[MW Upsell] Found handle from input:', handleInput.value);
      return handleInput.value;
    }
    
    console.log('[MW Upsell] Could not find product handle');
    return null;
  }

  function getVariantImage(product, variant) {
    // Try variant featured image first
    if (variant && variant.featured_image && variant.featured_image.src) {
      return variant.featured_image.src;
    }
    
    // Try to find image by variant ID
    if (product.images && variant) {
      const variantImage = product.images.find(img => 
        img.variant_ids && img.variant_ids.includes(variant.id)
      );
      
      if (variantImage && variantImage.src) {
        return variantImage.src;
      }
    }
    
    // Try product featured_image
    if (product.featured_image) {
      return product.featured_image;
    }
    
    // Fallback to first product image
    if (product.images && product.images.length > 0) {
      return product.images[0].src || product.images[0];
    }
    
    // Last resort: try product.image
    if (product.image) {
      return product.image;
    }
    
    console.warn('[MW Upsell] No image found for product:', product.title);
    return 'https://via.placeholder.com/80x80?text=No+Image';
  }

  function matchVariantByOption(product, referenceVariant) {
    // Try to match by first option (usually color)
    let targetOption = referenceVariant.option1;
    
    // Remove " / Schwarz" from the target option for matching
    // e.g. "Gold / Schwarz" → "Gold"
    if (targetOption && targetOption.includes(' / Schwarz')) {
      targetOption = targetOption.replace(' / Schwarz', '');
      console.log('[MW Upsell] Mapping variant:', referenceVariant.option1, '→', targetOption);
    }
    
    // Try exact match first
    let matched = product.variants.find(v => v.option1 === targetOption);
    
    // If not found and original had " / Schwarz", try with " / Schwarz" as well
    if (!matched && referenceVariant.option1.includes(' / Schwarz')) {
      matched = product.variants.find(v => v.option1 === referenceVariant.option1);
    }
    
    // Fallback to first variant
    return matched || product.variants[0];
  }

  function formatPrice(price) {
    // Shopify product APIs return price in cents
    // But sometimes it's already a string like "28.90"
    let euros;
    
    if (typeof price === 'string') {
      euros = parseFloat(price);
    } else {
      // Price in cents (e.g. 2890 → 28.90)
      euros = price / 100;
    }
    
    return euros.toFixed(2).replace('.', ',') + '€';
  }
  
  function resizeImage(url, width) {
    if (!url || url.includes('placeholder')) return url;
    
    // Shopify CDN URLs can be resized by adding ?width=X
    // Handle both cdn.shopify.com and //domain/cdn/shop/files/ URLs
    if (url.includes('cdn.shopify.com') || url.includes('/cdn/shop/')) {
      // Remove existing size parameters
      const baseUrl = url.split('?')[0];
      return `${baseUrl}?width=${width}`;
    }
    
    return url;
  }

  // ===== START =====
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();

